import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AdminUser, AdminActivity } from '../types/admin';

interface AdminAuthContextType {
  admin: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<{ requiresMfa?: boolean }>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateProfile: (profileData: { name: string; email: string; phone: string }) => Promise<void>;
  checkPermission: (permission: string) => boolean;
  logActivity: (action: AdminActivity['action'], target?: string) => void;
  error: string | null;
  clearError: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

interface AdminAuthProviderProps {
  children: ReactNode;
}

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing session on mount
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (token) {
          // Validate token with backend
          const response = await fetch(`${API_BASE_URL}/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setAdmin(userData);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem('admin_token');
          }
        }
      } catch (error) {
        console.error('Session validation failed:', error);
        localStorage.removeItem('admin_token');
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  const login = async (email: string, password: string, mfaCode?: string): Promise<{ requiresMfa?: boolean }> => {
    setIsLoading(true);
    setError(null);

    try {
      if (process.env.REACT_APP_ENVIRONMENT === 'development') {
        console.log('Attempting login with:', { email, password: '***', mfaCode: mfaCode ? '***' : 'none' });
        console.log('API URL:', `${API_BASE_URL}/login`);
      }

      // Authenticate with backend
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          mfaToken: mfaCode,
        }),
      });

      if (process.env.REACT_APP_ENVIRONMENT === 'development') {
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        if (process.env.REACT_APP_ENVIRONMENT === 'development') {
          console.error('Login failed:', errorData);
        }
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Check if MFA is required
      if (data.requiresMfa) {
        return { requiresMfa: true };
      }
      
      if (process.env.REACT_APP_ENVIRONMENT === 'development') {
        console.log('Login successful:', { user: data.user, tokenLength: data.token?.length });
      }
      
      // Store secure token
      localStorage.setItem('admin_token', data.token);
      
      // Set admin data
      setAdmin(data.user);
      
      // Log successful login
      logActivity('login');
      
      return {};
      
    } catch (error) {
      if (process.env.REACT_APP_ENVIRONMENT === 'development') {
        console.error('Login error:', error);
      }
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Log logout activity
      if (admin) {
        await logActivity('logout');
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Clear local state
      localStorage.removeItem('admin_token');
      setAdmin(null);
      setError(null);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }

      // Log password change activity
      logActivity('password_change');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password change failed';
      setError(errorMessage);
      throw error;
    }
  };

  const updateProfile = async (profileData: { name: string; email: string; phone: string }): Promise<void> => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const data = await response.json();
      
      // Update the admin state with the new profile data
      setAdmin(prev => prev ? { ...prev, ...data.user } : null);

      // Log profile update activity
      logActivity('profile_updated');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      setError(errorMessage);
      throw error;
    }
  };

  const checkPermission = (permission: string): boolean => {
    if (!admin) return false;
    
    // Super admins have all permissions
    if (admin.role === 'super_admin') return true;
    
    // Check specific permission
    return admin.permissions.includes(permission);
  };

  const logActivity = async (action: AdminActivity['action'], target?: string): Promise<void> => {
    try {
      // For now, just log to console
      console.log('🔍 Admin Activity:', { action, target, timestamp: new Date(), admin: admin?.email });
      
      // In the future, you can send activity logs to the backend
      // const token = localStorage.getItem('admin_token');
      // if (token) {
      //   await fetch(`${API_BASE_URL}/activity`, {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       'Authorization': `Bearer ${token}`,
      //     },
      //     body: JSON.stringify({
      //       action,
      //       target,
      //       timestamp: new Date(),
      //     }),
      //   });
      // }
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AdminAuthContextType = {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    login,
    logout,
    changePassword,
    updateProfile,
    checkPermission,
    logActivity,
    error,
    clearError,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};